import { useMemo } from "react";
import { useUser } from "../contexts/UserContext/UserContext";
import { ApiClient } from "@ucl/library";
import { createAuthenticatedApiClient } from "@/api/createAuthenticatedApiClient";

/**
 * Generic factory function to create any API client with authentication.
 *
 * @template T - The API client class to instantiate
 * @param ApiClientClass - The class constructor for the API client
 * @param tokenGetter - Function that returns the current access token
 * @returns An instance of the specified API client class with authentication
 */
export const createAuthenticatedApiClientOfType = <T>(
  ApiClientClass: new (apiClient: ApiClient) => T,
  tokenGetter: () => string | null
): T => {
  const apiClient = createAuthenticatedApiClient(tokenGetter);
  return new ApiClientClass(apiClient);
};

/**
 * Generic hook to provide an authenticated API client.
 *
 * @template T - The API client class
 * @param ApiClientClass - The API client class constructor
 * @returns An instance of the API client with auth interceptor
 */
export const useAuthenticatedApiClient = <T>(
  ApiClientClass: new (apiClient: ApiClient) => T
): T => {
  const { accessToken } = useUser();

  return useMemo(() => {
    return createAuthenticatedApiClientOfType(ApiClientClass, () => accessToken);
  }, [ApiClientClass, accessToken]);
};